/**
 * Screen — route-sheet detail, the mirror of `DecaDetailScreen`.
 *
 * Its own route rather than a branch of `/decas/$decaId`: a *hoja de ruta* is not
 * a DeCA. They are different legal instruments under different Orders
 * (FOM/1230/2013 against FOM/2861/2012), and the URL should not assert otherwise.
 *
 * Header (number · estado · generated), the QR panel, the four sections read-only in
 * the PDF's own order, the Historial and the version chain — the goods screen's
 * arrangement, because a user who knows one should not have to learn the other.
 *
 * The QR panel is the compliance-critical part: the URL it shows is the one printed on
 * the document, it downloads the PDF directly with no sign-in and no landing page (BOE
 * Third.3–4), and an in-place amendment leaves it **unchanged** (Fifth.1). The portal
 * shows it and copies it; it never wraps it in a screen of its own.
 *
 * 🚨 **The printing rule here is the inverse of the goods one, and must stay inverted.**
 * On the goods DeCA `nif_conductor` is captured and **never printed**
 * (`DICCIONARIO_CAMPOS` 17), and section ④ of `DecaDetailScreen` marks it *No se imprime
 * en el PDF*. On a route sheet the driver's name **and NIF are printed**: Art. 2(e) of
 * Orden FOM/1230/2013 makes both content of the sheet, and the Client instructed it in
 * writing on 2026-09-09 after our contrary recommendation and its reason were put to them
 * (Addendum No. 2, Clause 4.2 — a documented instruction of the Controller). Section ④
 * therefore carries `passengerCreate.driverOnPdf`, stating it out loud, and the
 * not-printed marker is rendered from `onPdf` so that it cannot appear here by accident.
 * Do not "fix" this to match goods, and do not relax the goods rule to match this.
 *
 * ⚠️ `from` is the route's **id**, not its path. Every screen here sits under two
 * pathless guards (`authenticated` and `deca-read-surface` in `router.tsx`), which
 * are part of the id and absent from the URL, so the two strings differ and only the
 * id typechecks. Shortening it to `/hojas-de-ruta/$sheetId` is a compile error, not a
 * tidy-up. The sibling detail screens dodge this with `useParams({ strict: false })`
 * and a cast; naming the route buys the param's type back, and moving this route to
 * another guard then fails here loudly instead of silently.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  ifMatch,
  unwrap,
  type PassengerDeca,
  type PassengerFieldError,
  type PassengerHistoryEntry,
} from '@deca/api'

import { api, downloadFile, MIME } from '../app/api.ts'
import {
  apiErrorMessage,
  isConflict,
  isResourceNotFound,
  passengerValidationProblemOf,
} from '../app/errors.ts'
import { useAuth } from '../app/auth.tsx'
import {
  usePassengerDeca,
  usePassengerDecaHistory,
  usePassengerDecaVersions,
  useInvalidatePassengerDeca,
} from '../app/queries.ts'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { offeredFamilies } from '../deca/transportType.ts'
import { actorText } from '../deca/actor.ts'
import {
  authenticatedDownloadAvailable,
  canComplete,
  canDeleteDraft,
  canAmend,
  canVoid,
  directDownloadLive,
  downloadWindowDays,
  isGenerated,
} from '../deca/estado.ts'
import { formatDateTime, formatHistoryValue, formatServiceDate } from '../deca/format.ts'
import {
  passengerFieldsForSection,
  PASSENGER_SECTION_NUMBER,
  type PassengerFieldDef,
} from '../deca/passenger/fields.ts'
import { AppShell } from '../shell/AppShell.tsx'
import {
  Button,
  Card,
  Datum,
  EmptyState,
  ErrorState,
  LoadingRows,
  Notice,
  SectionNumber,
} from '../ui/primitives.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { DocumentOwnerLine } from '../ui/DocumentOwnerLine.tsx'
import { EstadoChip, GroupedTag } from '../ui/EstadoChip.tsx'
import { Icon } from '../ui/Icon.tsx'
import { PassengerValidationSummary } from '../ui/PassengerValidationSummary.tsx'
import { QrPanel } from '../ui/QrPanel.tsx'
import { RowMenu } from '../ui/RowMenu.tsx'
import { TextAreaInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'
import { PassengerModifyDialog } from './PassengerModifyDialog.tsx'

export function PassengerDetailScreen() {
  const { t, i18n } = useTranslation()
  const { sheetId } = useParams({ from: '/authenticated/deca-read-surface/hojas-de-ruta/$sheetId' })
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidatePassengerDeca()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const { organisation: context } = useOrganisationChoice()

  const query = usePassengerDeca(sheetId)
  const sheet = query.data?.data
  const etag = query.data?.etag ?? null

  const history = usePassengerDecaHistory(sheetId, Boolean(sheet && isGenerated(sheet)))
  const versions = usePassengerDecaVersions(sheetId, Boolean(sheet && isGenerated(sheet)))

  const [modifyOpen, setModifyOpen] = useState(false)
  const [voidOpen, setVoidOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionFieldErrors, setActionFieldErrors] = useState<PassengerFieldError[] | undefined>()

  /** The newest version, for the "Modificado por" line in Detalles. */
  const lastVersion = useMemo(() => {
    const all = versions.data?.data ?? []
    return all.reduce<(typeof all)[number] | undefined>(
      (best, version) => (best && best.versionNo >= version.versionNo ? best : version),
      undefined,
    )
  }, [versions.data])

  /**
   * Columns touched by the newest version — the PDF's yellow highlight.
   *
   * ⚠️ Two maps, not the goods screen's one, and the difference is not cosmetic.
   * `conductor_nombre`, `conductor_nif` and `matricula_autobus` exist **both** on the
   * sheet and as a per-service override (BOE Sixth), so a single map keyed by `field`
   * would light up the sheet's own driver because service 2's driver changed. The goods
   * screen can key by field alone only because no consignment column shares a name with a
   * document column. `servicioSeq` is what separates the two here.
   */
  const lastChanged = useMemo(() => {
    const entries = history.data?.data ?? []
    const document = new Map<string, PassengerHistoryEntry>()
    const services = new Map<string, PassengerHistoryEntry>()
    if (entries.length === 0 || !sheet) return { document, services }
    const latest = Math.max(...entries.map((entry) => entry.versionNo))
    for (const entry of entries) {
      if (entry.versionNo !== latest) continue
      if (entry.servicioSeq == null) document.set(entry.field, entry)
      else services.set(`${entry.servicioSeq}.${entry.field}`, entry)
    }
    return { document, services }
  }, [history.data, sheet])

  async function runAction(action: () => Promise<unknown>, message: string) {
    setBusy(true)
    setActionError(null)
    setActionFieldErrors(undefined)
    try {
      await action()
      invalidate(sheetId)
      toast.success(message)
    } catch (error) {
      // 🚨 `generate` refuses with a **flat** 422 — `fieldErrors`, no parties and no
      // penalty notice, because FOM/1230/2013 has no liable-party split. The goods
      // `validationProblemOf` tests `Array.isArray(problem.partyErrors)` and answers
      // `null` for one of these; reading it through that helper would show a generic
      // message while the server had said exactly which fields are wrong.
      const problem = passengerValidationProblemOf(error)
      if (problem) {
        setActionFieldErrors(problem.fieldErrors)
      } else {
        setActionError(
          isConflict(error) ? t('passengerModify.versionConflict') : apiErrorMessage(error, t),
        )
      }
    } finally {
      setBusy(false)
    }
  }

  if (query.isLoading) {
    return (
      <AppShell title={t('passengerDetail.draftTitle')}>
        <Card>
          <LoadingRows rows={6} columns={3} />
        </Card>
      </AppShell>
    )
  }

  // A document that is **not there** is not a load failure (#17). `ErrorState` announces an
  // incident and offers a *Reintentar* that cannot ever succeed, because nothing about the
  // document is going to change. `isResourceNotFound` keeps this off the API's bare
  // `NOT_FOUND`, which means "no such route" and is a genuine failure worth retrying.
  if (isResourceNotFound(query.error)) {
    return (
      <AppShell title={t('passengerDetail.notFoundTitle')}>
        <Card>
          <EmptyState
            icon="search"
            title={t('passengerDetail.notFoundTitle')}
            body={t('passengerDetail.notFoundBody')}
            action={
              <Button variant="primary" onClick={() => void navigate({ to: '/decas' })}>
                {t('passengerDetail.notFoundAction')}
              </Button>
            }
          />
        </Card>
      </AppShell>
    )
  }

  if (query.error || !sheet) {
    return (
      <AppShell title={t('passengerDetail.draftTitle')}>
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </AppShell>
    )
  }

  const generated = isGenerated(sheet)
  // A rejection here used to go nowhere on the goods screen — `void` on an uncaught promise
  // — so a failed download was a button that did nothing at all. Say so instead.
  const downloadPdf = () => {
    void downloadFile(
      `/api/v1/passenger-decas/${encodeURIComponent(sheet.id)}/pdf`,
      MIME.pdf,
      `${sheet.documentNumber ?? 'hoja-de-ruta'}.pdf`,
    ).catch((error: unknown) => toast.error(apiErrorMessage(error, t)))
  }

  // The four secondary actions are each reachable twice — a button or a link on a desktop, an item
  // of «Más acciones» on a phone (#111). The three that act each have one body both call;
  // «Duplicar» only navigates, so its link and its item name the same route, and the tests pin
  // both. `busy` is checked here, not passed to the menu as `disabled`: `RowMenu` drops a disabled
  // item instead of greying it.
  const mayComplete = canComplete(sheet) && abilities.writeDecas
  const mayVoid = canVoid(sheet) && abilities.voidDecas
  const mayDeleteDraft = canDeleteDraft(sheet) && abilities.writeDecas
  // #178 — in every status: the copy is a new sheet and this one is only read. `writeDecas` alone.
  // #220: and only for a company offered route sheets.
  const mayDuplicate = abilities.writeDecas && offeredFamilies(principal).passenger
  const complete = () => {
    if (busy) return
    void runAction(
      () =>
        unwrap(
          api.POST('/api/v1/passenger-decas/{sheetId}/complete', {
            params: { path: { sheetId: sheet.id }, header: ifMatch(etag) },
          }),
        ),
      t('passengerDetail.completed'),
    )
  }
  const deleteDraft = () => {
    if (busy) return
    if (!window.confirm(t('detail.deleteDraftConfirm'))) return
    void runAction(
      () =>
        unwrap(
          api.DELETE('/api/v1/passenger-decas/{sheetId}', {
            params: { path: { sheetId: sheet.id } },
          }),
        ),
      t('passengerDetail.deleted'),
    ).then(() => navigate({ to: '/decas', search: {} }))
  }

  return (
    <AppShell
      title={
        <span className="doc-number">
          {generated ? sheet.documentNumber : t('passengerDetail.draftTitle')}
        </span>
      }
      // Whose document this is — for a role that can be inside more than one company, and
      // only for such a role. The parties on a route sheet are the carrier undertaking and
      // the hirer, which is a different question from which tenant issued it.
      subtitle={
        abilities.choosesOrganisation && context
          ? t('detail.organisation', { organisation: context.name })
          : undefined
      }
    >
      <div className="detail-actions">
        <EstadoChip estado={sheet.estado} voidReason={sheet.voidReason} />
        {sheet.isGrouped ? <GroupedTag count={sheet.services.length} /> : null}
        <span className="small muted">
          {generated
            ? `${t('common.generatedAt')} ${formatDateTime(sheet.generatedAt, i18n.language)} · ${t('common.version')} ${sheet.versionNo}`
            : `${t('common.createdAt')} ${formatDateTime(sheet.createdAt, i18n.language)}`}
        </span>

        <span className="spacer" />

        {/* On a phone the secondary actions are hidden and «Más acciones» offers them instead
            (#111): the primary action and the download stay one tap away. */}
        <div className="row wrap detail-actions__buttons" style={{ gap: 'var(--space-sm)' }}>
          {/* #90 — the way back into the form that made this draft. A `Link`, not a `Button`:
              it is a navigation to another screen, so it has to be middle-clickable, copyable
              and reachable as a link by a screen reader. Only while `BORRADOR` — an issued sheet
              changes through *Modificar* or *Sustituir*, both already on this row, and Seventh
              voids any other alteration. */}
          {!generated && abilities.writeDecas ? (
            <Link
              to="/hojas-de-ruta/$sheetId/editar"
              params={{ sheetId: sheet.id }}
              className="btn btn--ghost"
            >
              {t('common.edit')}
            </Link>
          ) : null}

          {!generated && abilities.writeDecas ? (
            <Button
              variant="primary"
              icon="check"
              disabled={busy}
              onClick={() =>
                void runAction(
                  () =>
                    unwrap(
                      api.POST('/api/v1/passenger-decas/{sheetId}/generate', {
                        params: { path: { sheetId: sheet.id }, header: ifMatch(etag) },
                      }),
                    ),
                  t('passengerDetail.generated', { documentNumber: sheet.documentNumber ?? '' }),
                )
              }
            >
              {t('passengerCreate.generate')}
            </Button>
          ) : null}

          {canAmend(sheet) && abilities.writeDecas ? (
            <Button variant="primary" icon="edit" onClick={() => setModifyOpen(true)}>
              {t('detail.modify')}
            </Button>
          ) : null}

          {authenticatedDownloadAvailable(sheet) ? (
            <Button icon="download" onClick={downloadPdf}>
              {t('detail.downloadPdf')}
            </Button>
          ) : null}

          {mayComplete ? (
            <Button
              className="detail-actions__secondary"
              icon="checkCircle"
              disabled={busy}
              onClick={complete}
            >
              {t('detail.complete')}
            </Button>
          ) : null}

          {/* #178 — «Duplicar». A `Link`, as «Editar» is; secondary, so a phone finds it in «Más
              acciones». */}
          {mayDuplicate ? (
            <Link
              to="/hojas-de-ruta/$sheetId/duplicar"
              params={{ sheetId: sheet.id }}
              className="btn btn--secondary detail-actions__secondary"
            >
              <Icon name="copy" size={16} />
              {t('duplicate.action')}
            </Link>
          ) : null}

          {mayVoid ? (
            <Button
              className="detail-actions__secondary"
              variant="danger"
              icon="ban"
              onClick={() => setVoidOpen(true)}
            >
              {t('detail.void')}
            </Button>
          ) : null}

          {mayDeleteDraft ? (
            <Button
              className="detail-actions__secondary"
              variant="danger"
              icon="trash"
              disabled={busy}
              onClick={deleteDraft}
            >
              {t('detail.deleteDraft')}
            </Button>
          ) : null}

          {/* The same secondary actions, for a phone. Hidden above 600 px by CSS; its items exist only
              while it is open, so the desktop DOM gains one closed trigger and no second
              copy of any action. */}
          {mayComplete || mayDuplicate || mayVoid || mayDeleteDraft ? (
            <div className="detail-actions__more">
              <RowMenu
                label={t('detail.moreActions')}
                items={[
                  {
                    key: 'complete',
                    label: t('detail.complete'),
                    icon: 'checkCircle',
                    disabled: !mayComplete,
                    onSelect: complete,
                  },
                  {
                    key: 'duplicate',
                    label: t('duplicate.action'),
                    icon: 'copy',
                    disabled: !mayDuplicate,
                    onSelect: () =>
                      void navigate({ to: '/hojas-de-ruta/$sheetId/duplicar', params: { sheetId: sheet.id } }),
                  },
                  {
                    key: 'void',
                    label: t('detail.void'),
                    icon: 'ban',
                    danger: true,
                    disabled: !mayVoid,
                    onSelect: () => setVoidOpen(true),
                  },
                  {
                    key: 'deleteDraft',
                    label: t('detail.deleteDraft'),
                    icon: 'trash',
                    danger: true,
                    disabled: !mayDeleteDraft,
                    onSelect: deleteDraft,
                  },
                ]}
              />
            </div>
          ) : null}
        </div>
      </div>

      {actionError ? <Notice tone="danger">{actionError}</Notice> : null}
      {actionFieldErrors ? <PassengerValidationSummary errors={actionFieldErrors} /> : null}

      {sheet.supersededByDocumentId ? (
        <Notice tone="warning">
          {t('estado.supersededBy', { documentNumber: '' })}{' '}
          <Link to="/hojas-de-ruta/$sheetId" params={{ sheetId: sheet.supersededByDocumentId }}>
            {sheet.supersededByDocumentId}
          </Link>
        </Notice>
      ) : null}
      {sheet.supersedesDocumentId ? (
        <Notice tone="info">
          {t('estado.supersedes', { documentNumber: '' })}{' '}
          <Link to="/hojas-de-ruta/$sheetId" params={{ sheetId: sheet.supersedesDocumentId }}>
            {sheet.supersedesDocumentId}
          </Link>
        </Notice>
      ) : null}

      {/* A generated sheet leads with its QR card in the DOM, so a phone, a screen reader and
          Tab reach the QR and the download first (#111); `detail-layout--qr-first` then places
          it back in the right-hand column on a desktop. A draft's card holds only a notice, so
          a draft keeps it in the aside. */}
      <div className={generated ? 'detail-layout detail-layout--qr-first' : 'detail-layout'}>
        {generated ? (
          <div className="detail-layout__qr">
            <Card title={t('detail.qrTitle')}>
              <QrPanel
                qrPath={`/api/v1/passenger-decas/${encodeURIComponent(sheet.id)}/qr`}
                documentNumber={sheet.documentNumber}
                directDownloadUrl={sheet.directDownloadUrl}
                csv={sheet.csv}
                live={directDownloadLive(sheet)}
                expiresLabel={
                  sheet.directDownloadExpiresAt
                    ? t('detail.expiresAt', {
                        date: formatDateTime(sheet.directDownloadExpiresAt, i18n.language),
                      })
                    : undefined
                }
                onDownloadPdf={downloadPdf}
              />
            </Card>
          </div>
        ) : null}

        <div className="stack detail-layout__main">
          <div className="detail-parties">
            <PartyCard section="transportista" sheet={sheet} changed={lastChanged.document} />
            <PartyCard section="contratante" sheet={sheet} changed={lastChanged.document} />
          </div>

          <Card
            title={
              <>
                <SectionNumber n={PASSENGER_SECTION_NUMBER.servicios} />
                {t('passengerSections.servicios')}
              </>
            }
            subtitle={t('passengerDetail.servicesCount', { count: sheet.services.length })}
            id="s-servicios"
          >
            <div className="stack">
              {sheet.services.map((row, index) => (
                <ServiceBlock
                  key={row.seq}
                  service={row}
                  seq={index + 1}
                  grouped={sheet.isGrouped}
                  changed={lastChanged.services}
                />
              ))}
            </div>
          </Card>

          <Card
            title={
              <>
                <SectionNumber n={PASSENGER_SECTION_NUMBER.transporte} />
                {t('passengerSections.transporte')}
              </>
            }
            id="s-transporte"
          >
            <div className="stack">
              {/* 🚨 Art. 2(e). Our own goods detail screen marks its driver NIF *No se
                  imprime en el PDF* one screen away, so this has to be said rather than
                  merely not contradicted. See the file docblock. */}
              <Notice tone="info">{t('passengerCreate.driverOnPdf')}</Notice>
              <div className="datum-grid">
                {passengerFieldsForSection('transporte').map((field) => (
                  <SheetDatum key={field.column} field={field} sheet={sheet} changed={lastChanged.document} />
                ))}
              </div>
            </div>
          </Card>

          <Card
            title={
              <>
                <SectionNumber n={5} />
                {t('detail.historial')}
              </>
            }
            subtitle={t('detail.versionsHelp')}
            id="s-historial"
            flush
          >
            {(history.data?.data ?? []).length === 0 ? (
              <p className="small muted" style={{ padding: 'var(--space-xl)' }}>
                {t('detail.historialEmpty')}
              </p>
            ) : (
              <div className="table-wrap">
                <table className="table table--stack">
                  <caption className="sr-only">{t('detail.historial')}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('detail.historialColumns.changedAt')}</th>
                      <th scope="col">{t('detail.historialColumns.field')}</th>
                      <th scope="col">{t('detail.historialColumns.description')}</th>
                      <th scope="col">{t('detail.historialColumns.usuario')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(history.data?.data ?? []).map((entry, index) => (
                      <tr key={`${entry.versionNo}-${entry.field}-${index}`}>
                        <td className="tabular" data-label={t('detail.historialColumns.changedAt')}>
                          {formatDateTime(entry.changedAt, i18n.language)}
                        </td>
                        <td data-label={t('detail.historialColumns.field')}>
                          {entry.fieldLabelEs}
                          {entry.servicioSeq != null ? (
                            <span className="muted">
                              {' '}
                              · {t('passengerCreate.service', { seq: entry.servicioSeq })}
                            </span>
                          ) : null}
                          <div className="small muted">
                            <s>{formatHistoryValue(entry.oldValue, i18n.language, entry.oldHasTime) ?? '—'}</s> →{' '}
                            {formatHistoryValue(entry.newValue, i18n.language, entry.newHasTime) ?? '—'}
                          </div>
                        </td>
                        <td data-label={t('detail.historialColumns.description')}>
                          {entry.descriptionEs}
                        </td>
                        {/* The typed actor, not `usuario`: the same string, but only the type says it
                            came from a linked phone (#182, as the goods screen since #110). */}
                        <td data-label={t('detail.historialColumns.usuario')}>
                          {actorText(entry.actor, t)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <aside className="stack detail-layout__aside">
          {!generated ? (
            <Card title={t('detail.qrTitle')}>
              <Notice tone="neutral">{t('create.railHelp')}</Notice>
            </Card>
          ) : null}

          <Card title={t('detail.details')}>
            <div className="stack-sm small">
              <div className="row-between">
                <span className="muted">{t('list.columns.estado')}</span>
                <EstadoChip estado={sheet.estado} voidReason={sheet.voidReason} />
              </div>
              <Datum
                label={t('detail.type')}
                value={
                  sheet.isGrouped
                    ? t('passengerCreate.typeGrouped', { count: sheet.services.length })
                    : t('passengerCreate.typeSingle')
                }
              />
              <Datum
                label={t('common.createdBy')}
                value={
                  sheet.createdBy
                    ? `${actorText(sheet.createdBy, t)} · ${formatDateTime(sheet.createdAt, i18n.language)}`
                    : undefined
                }
              />
              {/* Addendum No. 3, Point 7. The line is for administrators alone (design spec §10): every
                  other role is handed no owner to show, although the body carries one for any reader. */}
              <DocumentOwnerLine
                owner={abilities.administer ? sheet.owner : null}
                subject={sheet}
                canReassign={abilities.administer}
                reassign={(userId) =>
                  unwrap(
                    api.POST('/api/v1/passenger-decas/{sheetId}/reassign', {
                      params: { path: { sheetId: sheet.id } },
                      body: { userId },
                    }),
                  )
                }
                onReassigned={() => {
                  invalidate(sheet.id)
                  toast.success(t('owner.reassigned'))
                }}
              />
              {lastVersion ? (
                <Datum
                  label={t('detail.modifiedBy')}
                  value={`${actorText(lastVersion.createdBy, t)} · ${formatDateTime(lastVersion.createdAt, i18n.language)}`}
                />
              ) : null}
              <Datum
                label={t('detail.directDownload')}
                value={t('detail.days', { count: downloadWindowDays(principal) })}
                hint={
                  sheet.directDownloadExpiresAt
                    ? t('detail.expiresAt', {
                        date: formatDateTime(sheet.directDownloadExpiresAt, i18n.language),
                      })
                    : undefined
                }
              />
              {/*
                BOE Second.4 names a **single** obligor for passengers — "the transport
                undertakings" — against two for goods, so there is no per-party retention
                split here. The date, not a window: `retainUntil` is computed from the
                organisation's own `retentionMonths` and is what the retention sweep enforces.
              */}
              <Datum
                label={t('detail.retention')}
                value={
                  sheet.retainUntil
                    ? t('detail.retainUntil', {
                        date: formatDateTime(sheet.retainUntil, i18n.language),
                      })
                    : t('common.none')
                }
              />
              {sheet.externalRef ? (
                <Datum label={t('fields.externalRef.label')} value={sheet.externalRef} />
              ) : null}
              {sheet.pdf ? (
                <Datum
                  label="PDF"
                  value={`${Math.round((sheet.pdf.bytes ?? 0) / 1024)} kB`}
                  tabular
                />
              ) : null}
            </div>
          </Card>
        </aside>
      </div>

      {modifyOpen ? (
        <PassengerModifyDialog
          sheet={sheet}
          etag={etag}
          open={modifyOpen}
          onClose={() => setModifyOpen(false)}
        />
      ) : null}

      <VoidDialog
        sheet={sheet}
        etag={etag}
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        onDone={() => {
          invalidate(sheetId)
          toast.success(t('passengerDetail.voided'))
        }}
      />
    </AppShell>
  )
}

/** Sections ① and ② — the carrier undertaking and the hirer, in the Order's own order. */
function PartyCard({
  section,
  sheet,
  changed,
}: {
  section: 'transportista' | 'contratante'
  sheet: PassengerDeca
  changed: ReadonlyMap<string, PassengerHistoryEntry>
}) {
  const { t } = useTranslation()
  return (
    <Card
      title={
        <>
          <SectionNumber n={PASSENGER_SECTION_NUMBER[section]} />
          {t(`passengerSections.${section}`)}
        </>
      }
      id={`s-${section}`}
    >
      <div className="datum-grid">
        {passengerFieldsForSection(section).map((field) => (
          <SheetDatum key={field.column} field={field} sheet={sheet} changed={changed} />
        ))}
      </div>
    </Card>
  )
}

/**
 * One of the sheet's own values.
 *
 * 🚨 The *No se imprime en el PDF* marker is rendered from `field.onPdf` rather than from a
 * hardcoded column list, which is what keeps this screen from ever contradicting the
 * dictionary. Every passenger field is `onPdf: true` today — including `conductor_nombre`
 * and `conductor_nif`, the exact inverse of the goods rule — so the marker renders nowhere
 * on a route sheet, and `passenger-detail-screen.test.tsx` asserts that it does not.
 */
function SheetDatum({
  field,
  sheet,
  changed,
}: {
  field: PassengerFieldDef
  sheet: PassengerDeca
  changed: ReadonlyMap<string, PassengerHistoryEntry>
}) {
  const { t } = useTranslation()
  return (
    <Datum
      label={
        <>
          {t(`fields.${field.column}.label`)}
          {!field.onPdf ? <span className="muted"> · {t('create.notPrinted')}</span> : null}
        </>
      }
      value={(sheet as unknown as Record<string, string | undefined>)[field.column]}
      modified={changed.has(field.column)}
      previousValue={changed.get(field.column)?.oldValue ?? null}
    />
  )
}

/**
 * Section ③'s repeatable body — one service.
 *
 * The three BOE Sixth overrides are shown only when this service actually departs from the
 * sheet: null there means "the route sheet's own driver and bus", and printing three empty
 * rows on every service would read as three missing values.
 */
function ServiceBlock({
  service,
  seq,
  grouped,
  changed,
}: {
  service: PassengerDeca['services'][number]
  seq: number
  grouped: boolean
  changed: ReadonlyMap<string, PassengerHistoryEntry>
}) {
  const { t, i18n } = useTranslation()
  const overridden = ['conductor_nombre', 'conductor_nif', 'matricula_autobus'].filter(
    (column) => (service as unknown as Record<string, string | null | undefined>)[column],
  )

  const datum = (column: string, value: string | null | undefined, tabular?: boolean) => {
    const entry = changed.get(`${seq}.${column}`)
    return (
      <Datum
        key={column}
        label={t(`fields.${column}.label`)}
        value={value}
        tabular={tabular}
        modified={Boolean(entry)}
        previousValue={entry?.oldValue ?? null}
        previousHasTime={entry?.oldHasTime}
      />
    )
  }

  return (
    <div className="service">
      {grouped ? (
        <div className="service__header">
          <span className="service__seq">
            <span className="service__badge">{seq}</span>
            {t('passengerCreate.service', { seq })}
          </span>
        </div>
      ) : null}

      <div className="datum-grid">
        {datum('servicio_origen', service.servicio_origen)}
        {datum('servicio_destino', service.servicio_destino)}
        {/* The flag, never the hour (#102): a service with no hour is its day's 00:00, and so is a
            midnight departure. */}
        {datum('servicio_fecha', formatServiceDate(service.servicio_fecha, service.servicioFechaHasTime, i18n.language), true)}
        {datum(
          'servicio_clase',
          service.servicio_clase ? t(`serviceClass.${service.servicio_clase}`) : null,
        )}
        {datum('servicio_paradas', service.servicio_paradas)}
        {datum('servicio_clase_ref', service.servicio_clase_ref)}
      </div>

      {overridden.length > 0 ? (
        <>
          <p className="small muted">{t('passengerDetail.overrides')}</p>
          <div className="datum-grid">
            {overridden.map((column) =>
              datum(column, (service as unknown as Record<string, string | null>)[column]),
            )}
          </div>
        </>
      ) : (
        <p className="small muted">{t('passengerDetail.driverOnSheet')}</p>
      )}
    </div>
  )
}

function VoidDialog({
  sheet,
  etag,
  open,
  onClose,
  onDone,
}: {
  sheet: PassengerDeca
  etag: string | null
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    if (reason.trim().length < 3) {
      setError(t('errors.required'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.POST('/api/v1/passenger-decas/{sheetId}/void', {
          params: { path: { sheetId: sheet.id }, header: ifMatch(etag) },
          body: { reason: reason.trim() },
        }),
      )
      onDone()
      onClose()
    } catch (caught) {
      setError(
        isConflict(caught) ? t('passengerModify.versionConflict') : apiErrorMessage(caught, t),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={t('passengerModify.voidTitle', { documentNumber: sheet.documentNumber ?? '' })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            icon="ban"
            loading={busy}
            disabled={busy}
            onClick={() => void submit()}
          >
            {t('passengerModify.voidSubmit')}
          </Button>
        </>
      }
    >
      <p className="small">{t('modify.voidIntro')}</p>
      <Notice tone="warning">{t('modify.voidWarning')}</Notice>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <TextAreaInput
        label={t('modify.voidReason')}
        required
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
    </Dialog>
  )
}
